import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHotkeys } from "react-hotkeys-hook";
import {
  Activity,
  AlertCircle,
  BarChart3,
  ClipboardList,
  Cpu,
  History,
  LayoutDashboard,
  Package,
  Users,
  Search,
} from "lucide-react";
import { Input } from "./ui/input";
import { useAuthStore } from "@/stores/auth.store";
import { cn } from "@/lib/utils";

interface Command {
  id: string;
  label: string;
  hint?: string;
  to: string;
  icon: typeof Activity;
  gestorOnly?: boolean;
}

const COMMANDS: Command[] = [
  { id: "dashboard", label: "Ir para Dashboard", hint: "g d", to: "/", icon: LayoutDashboard },
  { id: "lines", label: "Ir para Linhas", hint: "g l", to: "/lines", icon: Activity },
  { id: "products", label: "Ir para Produtos", to: "/products", icon: Package },
  { id: "data-entry", label: "Entrada de Dados", to: "/data-entry", icon: ClipboardList },
  { id: "devices", label: "CLPs & Sensores", to: "/devices", icon: Cpu, gestorOnly: true },
  { id: "alarms", label: "Alarmes", to: "/alarms", icon: AlertCircle },
  { id: "simulations", label: "Simulações", to: "/simulations", icon: BarChart3 },
  { id: "users", label: "Usuários", to: "/users", icon: Users, gestorOnly: true },
  { id: "audit", label: "Auditoria", to: "/audit", icon: History, gestorOnly: true },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  useHotkeys(
    "mod+k",
    (e) => {
      e.preventDefault();
      setOpen((v) => !v);
    },
    { enableOnFormTags: true }
  );
  useHotkeys("escape", () => setOpen(false), { enableOnFormTags: true });
  useHotkeys("g+d", () => navigate("/"));
  useHotkeys("g+l", () => navigate("/lines"));

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync intencional: limpa query/cursor quando palette fecha
      setQuery("");
      setSelectedIndex(0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const all = COMMANDS.filter((c) => !c.gestorOnly || user?.role === "GESTOR");
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => c.label.toLowerCase().includes(q));
  }, [query, user?.role]);

  function runCommand(c: Command) {
    setOpen(false);
    navigate(c.to);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const c = filtered[selectedIndex];
      if (c) runCommand(c);
    }
  }

  if (!open) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- backdrop overlay do command palette; Escape fecha via window listener
    <div
      className="fixed inset-0 z-[60] flex animate-fade-in items-start justify-center bg-background/70 p-4 pt-24"
      role="dialog"
      aria-modal="true"
      aria-label="Paleta de comandos"
      onClick={() => setOpen(false)}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- inner content do palette; eventos validos para impedir bubble */}
      <div
        className="w-full max-w-xl animate-scale-in rounded-lg border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            // eslint-disable-next-line jsx-a11y/no-autofocus -- primary input do CommandPalette eh foco esperado por UX
            autoFocus
            placeholder="Buscar comando ou página…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            className="border-0 px-0 focus-visible:ring-0"
          />
          <kbd className="hidden rounded border bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground sm:inline">
            esc
          </kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto p-1" role="listbox">
          {filtered.length === 0 ? (
            <li className="px-3 py-4 text-sm text-muted-foreground">
              Nenhum comando corresponde a “{query}”.
            </li>
          ) : (
            filtered.map((c, i) => {
              const Icon = c.icon;
              return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- li com onClick; ArrowUp/Down/Enter gerenciados no container parent
                <li
                  key={c.id}
                  role="option"
                  aria-selected={i === selectedIndex}
                  onMouseEnter={() => setSelectedIndex(i)}
                  onClick={() => runCommand(c)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm",
                    i === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
                  )}
                >
                  <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <span className="flex-1">{c.label}</span>
                  {c.hint ? (
                    <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                      {c.hint}
                    </kbd>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
        <footer className="border-t px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <span className="mr-3">↑↓ navegar</span>
          <span className="mr-3">⏎ abrir</span>
          <span>⌘K alternar</span>
        </footer>
      </div>
    </div>
  );
}
